import { ContextLossRecovery, type RecoverableSession } from "./ContextLossRecovery";
import {
  collectOffscreenGLCanvases,
  getOrCreateSharedRenderingEngine,
} from "../utils/renderingEngine";
import { logger } from "../utils/logger";

/** Minimal duck-typed surface the workspace needs from the rendering engine. */
export interface WorkspaceEngine {
  destroy(): void;
}

export interface ViewportWorkspaceDeps {
  /** Create (or recreate) the shared rendering engine. Production: getOrCreateSharedRenderingEngine (+ tool group setup). */
  createEngine: () => WorkspaceEngine;
  /** Attach NON-once webglcontextlost/restored listeners; invoke handlers.onLost/onRestored.
   *  Production preventDefault()s on loss to allow restoration. Returns a detach fn.
   *  onRestored may return a Promise (async recovery); callers may await it in tests. */
  attachContextLossListeners: (
    engine: WorkspaceEngine,
    handlers: { onLost: () => void; onRestored: () => Promise<void> },
  ) => () => void;
  onStatusChange: (status: "ok" | "recovering") => void;
  onLog: (level: "info" | "warn" | "error", code: string, detail: Record<string, unknown>) => void;
  downgradeThreshold?: number;
}

export class ViewportWorkspace {
  private engine: WorkspaceEngine | null = null;
  private sessions = new Map<string, RecoverableSession>();
  private detachListeners: (() => void) | null = null;
  private recovery: ContextLossRecovery;

  constructor(private deps: ViewportWorkspaceDeps) {
    this.recovery = new ContextLossRecovery({
      sessions: () => Array.from(this.sessions.values()),
      rebuildEngine: () => this.rebuildEngine(),
      onStatusChange: deps.onStatusChange,
      onLog: deps.onLog,
      downgradeThreshold: deps.downgradeThreshold,
    });
  }

  mount(): void {
    this.engine = this.deps.createEngine();
    this.detachListeners = this.deps.attachContextLossListeners(this.engine, {
      onLost: () => this.recovery.onLost(),
      onRestored: () => this.recovery.onRestored(),
    });
  }

  addSession(session: RecoverableSession): void {
    this.sessions.set(session.id, session);
  }

  removeSession(id: string): void {
    // Registry only — never destroys the engine.
    this.sessions.delete(id);
  }

  private rebuildEngine(): void {
    // Detach listeners from the old engine before recreating.
    this.detachListeners?.();
    this.engine = this.deps.createEngine();
    this.detachListeners = this.deps.attachContextLossListeners(this.engine, {
      onLost: () => this.recovery.onLost(),
      onRestored: () => this.recovery.onRestored(),
    });
  }

  dispose(): void {
    // The ONLY place the engine is destroyed.
    this.detachListeners?.();
    this.detachListeners = null;
    this.engine?.destroy();
    this.engine = null;
    this.sessions.clear();
  }
}

export interface ProductionWorkspaceDepsOpts {
  onStatusChange?: (status: "ok" | "recovering") => void;
  onLog?: (level: "info" | "warn" | "error", code: string, detail: Record<string, unknown>) => void;
  downgradeThreshold?: number;
}

/**
 * Wire the real Cornerstone APIs into a ViewportWorkspaceDeps.
 * Behavior is verified at A4 when this is connected to the app.
 */
export function createProductionWorkspaceDeps(
  opts: ProductionWorkspaceDepsOpts = {},
): ViewportWorkspaceDeps {
  return {
    createEngine: () => {
      // TODO(A4): register shared tool group via configureToolGroup
      return getOrCreateSharedRenderingEngine();
    },

    attachContextLossListeners: (engine, handlers) => {
      const canvases = collectOffscreenGLCanvases(engine);
      if (canvases.length === 0) {
        logger.warn(
          "[ViewportWorkspace] attachContextLossListeners: no offscreen GL canvases found — context loss will not be recoverable",
        );
      }

      const onLost = (event: Event) => {
        // preventDefault() tells the browser to allow the context to be
        // restored. This is the key improvement over the fatal once:true
        // handler in renderingEngine.ts.
        event.preventDefault();
        handlers.onLost();
      };
      const onRestored = () => {
        // Fire-and-forget in the browser event context; errors are logged by
        // ContextLossRecovery internally — no silent swallow here.
        void handlers.onRestored().catch((err: unknown) => {
          logger.error("[ViewportWorkspace] onRestored recovery failed", err);
        });
      };

      for (const canvas of canvases) {
        canvas.addEventListener("webglcontextlost", onLost);
        canvas.addEventListener("webglcontextrestored", onRestored);
      }

      return () => {
        for (const canvas of canvases) {
          try {
            canvas.removeEventListener("webglcontextlost", onLost);
            canvas.removeEventListener("webglcontextrestored", onRestored);
          } catch (err) {
            logger.warn("[ViewportWorkspace] detach: failed to remove context listeners", err);
          }
        }
      };
    },

    onStatusChange:
      opts.onStatusChange ??
      ((status) => {
        logger.info("[ViewportWorkspace] status →", status);
      }),

    onLog:
      opts.onLog ??
      ((level, code, detail) => {
        try {
          const msg = `[ViewportWorkspace] ${code}`;
          if (level === "error") {
            logger.error(msg, detail);
          } else if (level === "warn") {
            logger.warn(msg, detail);
          } else {
            logger.info(msg, detail);
          }
        } catch (err) {
          logger.error("[ViewportWorkspace] onLog handler threw", err);
        }
      }),

    downgradeThreshold: opts.downgradeThreshold,
  };
}
